import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, Calendar, Users, Building2, FileText, LogOut, Menu,
  ChevronRight, MessageSquare, Radio, GraduationCap, LayoutGrid, Crown, Hash, Music, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://game-invoice.preview.emergentagent.com/api/files/official-whistle/branding/67ca9f95-5248-441e-9eb3-5fc8cf5c72fb/47eefe68-1dae-481a-a902-450e03018adb.png";

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'assignor'] },
    { path: '/games', label: 'Games', icon: Calendar, roles: ['admin', 'assignor'] },
    { path: '/game-board', label: 'Game Board', icon: LayoutGrid, roles: ['admin', 'assignor'] },
    { path: '/invoices', label: 'Invoices', icon: FileText, roles: ['admin', 'assignor'] },
    { path: '/referees', label: 'Referees', icon: Users, roles: ['admin', 'assignor'] },
    { path: '/clients', label: 'Clients', icon: Building2, roles: ['admin', 'assignor'] },
    { path: '/admin/inquiries', label: 'Inquiries', icon: MessageSquare, roles: ['admin', 'assignor'] },
    { divider: true, roles: ['admin'] },
    { path: '/community', label: 'Community', icon: Hash, roles: ['admin', 'assignor'] },
    { path: '/livestream', label: 'Live & Podcast', icon: Radio, roles: ['admin'] },
    { path: '/messages', label: 'Messages', icon: MessageSquare, roles: ['admin'] },
    { path: '/ref-camp', label: 'Ref Camp', icon: GraduationCap, roles: ['admin', 'assignor'] },
    { path: '/subscriptions', label: 'Subscriptions', icon: Crown, roles: ['admin'] },
    { path: '/admin/playlist', label: 'Playlist', icon: Music, roles: ['admin'] },
    { path: '/admin/homepage', label: 'Homepage Editor', icon: ImageIcon, roles: ['admin'] },
    { path: '/admin/team', label: 'Team', icon: Users, roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles?.includes(user?.role));

  return (
    <div className="min-h-screen bg-background flex">
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-zinc-950 border-r border-zinc-800
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        overflow-y-auto
      `}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-zinc-800">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Logo" fetchpriority="high" decoding="async" loading="eager" className="w-10 h-10" />
              <div>
                <span className="font-heading font-bold text-sm uppercase tracking-wider block">Official With</span>
                <span className="font-heading font-bold text-sm uppercase tracking-wider block text-zinc-400">The Whistle</span>
              </div>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {filteredNavItems.map((item, index) => {
              if (item.divider) {
                return <div key={index} className="my-4 border-t border-zinc-800" />;
              }
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-sm
                    transition-all duration-200
                    ${isActive 
                      ? 'bg-white text-black font-medium' 
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-900'
                    }
                  `}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-zinc-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                <span className="font-heading font-bold text-lg">{user?.name?.charAt(0)?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-h-screen">
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-zinc-800 bg-zinc-950">
          <button
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
            className="p-2 hover:bg-zinc-900 rounded-sm"
          >
            <Menu className="w-6 h-6" />
          </button>
          <img src={LOGO_URL} alt="Logo" fetchpriority="high" decoding="async" loading="eager" className="w-8 h-8" />
          <div className="w-10" />
        </header>

        <div className="flex-1 p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export const PortalLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isReferee = user?.role === 'referee';

  const navLinks = isReferee ? [
    { path: '/referee-portal', label: 'My Portal' },
    { path: '/game-board', label: 'Game Board' },
    { path: '/community', label: 'Community' },
    { path: '/messages', label: 'Messages' },
    { path: '/ref-camp', label: 'Ref Camp' },
  ] : [
    { path: '/client-portal', label: 'Invoices' },
    { path: '/subscriptions', label: 'Watch Games' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-zinc-800 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to={isReferee ? '/referee-portal' : '/client-portal'} className="flex items-center gap-3">
                <img src={LOGO_URL} alt="Logo" fetchpriority="high" decoding="async" loading="eager" className="w-10 h-10" />
                <span className="font-heading font-bold uppercase tracking-wider hidden sm:block">
                  Official With The Whistle
                </span>
              </Link>
              
              <nav className="hidden md:flex items-center gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-3 py-2 text-sm rounded-sm transition-colors ${
                      location.pathname === link.path
                        ? 'bg-zinc-800 text-white'
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-zinc-400 hidden sm:block">{user?.name}</span>
              <Button
                variant="ghost"
                onClick={handleLogout}
                data-testid="portal-logout-btn"
                className="text-zinc-400 hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
        
        {/* Mobile Nav */}
        <div className="md:hidden border-t border-zinc-800 px-4 py-2 flex gap-2 overflow-x-auto">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-3 py-1.5 text-sm whitespace-nowrap rounded-sm ${
                location.pathname === link.path
                  ? 'bg-zinc-800 text-white'
                  : 'text-zinc-400'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};
